import {BehaviorSubject, Observable, map} from 'rxjs';
import {LayerRegisterInfo, RegisterOptions} from './types';

import styles from './layer.module.less';

type LayerRegisterExtraInfo = LayerRegisterInfo & {
	isActive: boolean;
}

class Manager {
	public readonly defaultPriority = 1050;

	public get hasRegisterModals(): boolean {
		return !!this.modals.length;
	}

	public get currentActive(): Readonly<LayerRegisterInfo> {
		return this.modals.filter(m => m.isActive).sort((a, b) => b.priority - a.priority)[0];
	}

	public get currentActive$(): Observable<Readonly<LayerRegisterInfo>> {
		return this.innerModals.pipe(map(m => this.currentActive));
	}

	public get currentMaxPriority(): number {
		const maxFromRegistered = this.modals
			.filter(modal => modal.stackOver)
			.map(modal => modal.priority)
			.reduce((a, b) => Math.max(a, b), 0);
		return (maxFromRegistered || this.defaultPriority) + 1;
	}

	constructor() {
		this.innerModals.subscribe(m => {
			if (typeof document !== 'undefined') {
				const body = document.body;
				const locked = body.classList.contains(styles.lockedScrollByLayer);
				if(m.filter(l => l.isActive).length) {
					if(!locked) {
						body.classList.add(styles.lockedScrollByLayer);
					}
				} else {
					if(locked) {
						body.classList.remove(styles.lockedScrollByLayer);
					}
				}
			}
		})
	}

	private innerModals: BehaviorSubject<LayerRegisterExtraInfo[]> = new BehaviorSubject<LayerRegisterExtraInfo[]>([]);

	protected set modals(value: LayerRegisterExtraInfo[]) {
		this.innerModals.next(value);
	}

	protected get modals(): LayerRegisterExtraInfo[] {
		return this.innerModals.value;
	}

	public register({desirePriority, active, preventStackOver}: RegisterOptions): Readonly<LayerRegisterInfo> {
		const registerInfo = {
			priority: this.currentMaxPriority,
			stackOver: !preventStackOver,
			isActive: active,
		} as LayerRegisterExtraInfo;

		registerInfo.activate = () => this.updateActivateStatus(registerInfo, true);
		registerInfo.deactivate = () => this.updateActivateStatus(registerInfo, false);

		if (desirePriority) {
			registerInfo.priority = desirePriority;
		}

		this.modals = [...this.modals, registerInfo];
		return registerInfo;
	}

	public deregister(registerInfo: LayerRegisterInfo | Readonly<LayerRegisterInfo>): void {
		this.modals = this.modals.filter(m => m !== registerInfo);
	}

	protected updateActivateStatus = (registerInfo: LayerRegisterExtraInfo, status: boolean): void => {
		registerInfo.isActive = status;
		this.modals = [...this.modals];
	};
}

export const LayerManager = new Manager();
